/**
 * Status components (StatusIndicator, StatusBadge)
 */

import { Badge, type BadgeProps } from './badge';
import { cn } from '@/lib/utils';

// Extended status types to cover all use cases
export type StatusType = 
  | 'success' | 'warning' | 'error' | 'info' | 'default'
  | 'active' | 'draft' | 'deprecated'
  | 'healthy';

// =============================================================================
// Status Indicator (dot)
// =============================================================================

export interface StatusIndicatorProps {
  status: StatusType;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusColors: Record<StatusType, string> = {
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
  default: 'bg-gray-400',
  active: 'bg-green-500',
  draft: 'bg-gray-400',
  deprecated: 'bg-red-500',
  healthy: 'bg-green-500',
};

const sizeDotClasses = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
  lg: 'h-2.5 w-2.5',
};

const sizeTextClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

export function StatusIndicator({
  status,
  label,
  size = 'md',
  className,
}: StatusIndicatorProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span
        className={cn(
          'rounded-full',
          sizeDotClasses[size],
          statusColors[status]
        )}
      />
      {label && <span className={sizeTextClasses[size]}>{label}</span>}
    </div>
  );
}

// =============================================================================
// Status Badge
// =============================================================================

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  status: StatusType;
  label?: string;
}

const statusVariants: Record<StatusType, BadgeProps['variant']> = {
  success: 'default',
  warning: 'secondary',
  error: 'destructive',
  info: 'outline',
  default: 'secondary',
  active: 'default',
  draft: 'secondary',
  deprecated: 'destructive',
  healthy: 'default',
};

export function StatusBadge({
  status,
  label,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  const displayText = label || children;
  
  return (
    <Badge
      variant={statusVariants[status]}
      className={cn(
        (status === 'success' || status === 'active' || status === 'healthy') && 
          'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
        status === 'warning' && 
          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
        (status === 'info') && 
          'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
        (status === 'draft' || status === 'default') && 
          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
        className
      )}
      {...props}
    >
      {displayText}
    </Badge>
  );
}
